"use client";

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

/**
 * Page prototypes — assemblies, not components. PGE-001 strings the board's
 * strongest real material into one scrolling story a cold reader can follow;
 * PGE-002 stages a single governed turn as choreography. Both speak the plain
 * register from TON-017 and the settled surface language: light sections on
 * the canonical ground, dark product assets floating inside them.
 */
import { ArtifactCard } from "@product/components/app/runs/artifact-card";
import { GovernedNonAnswerableCard } from "@product/components/app/runs/governed-non-answerable-card";
import { RunSourcesUsedView } from "@product/components/app/runs/run-sources-used-view";
import {
  markdownRehypePlugins,
  markdownRemarkPlugins,
} from "@product/components/content/markdown-math";
import { normalizeMarkdownContent } from "@product/components/content/markdown-normalize";
import { staticMarkdownRendererComponents } from "@product/components/content/markdown-renderer-components-static";
import { useStreamingMarkdownRehypePlugins } from "@product/components/app/runs/assistant-response-streaming";

import { DATA_LABEL_CLS, DISPLAY, EDITORIAL_ACTION_CLS, LABEL_CLS, ProductBridge } from "@/components/editorial";

import type { ConceptGroup } from "../spec";
import { RecordTableCompact, SPRAWL_SLIPS } from "./channels";
import {
  RECEIPT_PLUGINS,
  RECEIPT_SOURCES,
  RECEIVABLES_ARTIFACTS,
  RECEIVABLES_QUESTION,
  REFUSAL_FIXTURE,
} from "./product-fixtures";
import { CONDENSED_ANSWER_MARKDOWN, REFUSAL_OUTCOME } from "./sessions";

const INK_58 = "text-[color-mix(in_srgb,var(--theme-ink)_58%,transparent)]";
const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";
const GINK_82 = "text-[color-mix(in_srgb,var(--theme-ground)_82%,transparent)]";

/* ================================================================= */
/*  Shared page furniture                                             */
/* ================================================================= */

/** The governed answer through the real pipeline, condensed. */
export function CondensedAnswer({ children }: { children: string }) {
  return (
    <div className="typeset typeset-chat max-w-[58ch] break-words whitespace-normal">
      <ReactMarkdown
        remarkPlugins={markdownRemarkPlugins}
        rehypePlugins={markdownRehypePlugins}
        components={staticMarkdownRendererComponents}
      >
        {normalizeMarkdownContent(children)}
      </ReactMarkdown>
    </div>
  );
}

export function QuestionBubble({ children }: { children: string }) {
  return (
    <div className="flex justify-end">
      <p
        className="m-0 w-fit max-w-[90%] text-sm leading-relaxed break-words"
        style={{
          background: "hsl(var(--card))",
          color: "hsl(var(--foreground))",
          borderRadius: "18px 18px 4px 18px",
          padding: "8px 14px",
        }}
      >
        {children}
      </p>
    </div>
  );
}

/** Editorial CTA pair — real links (cross-page anchors work everywhere). */
export function CtaPair({ onDark = false }: { onDark?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a
        href="#waitlist"
        className={`${EDITORIAL_ACTION_CLS} ${
          onDark
            ? "bg-[var(--theme-ground)] text-[var(--theme-ink)]"
            : "bg-[var(--theme-dark)] text-[var(--theme-ground)]"
        }`}
      >
        Discuss your first workflow
      </a>
      <a
        href="/#how-it-works"
        className={`${EDITORIAL_ACTION_CLS} border ${
          onDark
            ? `border-[color-mix(in_srgb,var(--theme-ground)_36%,transparent)] ${GINK_82}`
            : `border-[var(--rule)] ${INK_80}`
        }`}
      >
        How it works
      </a>
    </div>
  );
}

/** A band's left rail: number, head, plain-words body. */
export function BandRail({
  index,
  head,
  children,
  headingLevel = "h3",
}: {
  index?: string;
  /** Rich content allows hand-drawn marks on a word (concept SKT). */
  head: ReactNode;
  children: ReactNode;
  headingLevel?: "h2" | "h3";
}) {
  const Heading = headingLevel;
  return (
    <div className="max-w-[38rem]">
      {index ? <p className={`${LABEL_CLS} text-[var(--theme-accent-text)]`}>{index}</p> : null}
      <Heading
        style={DISPLAY}
        className={`${INK_88} ${index ? "mt-3" : ""} max-w-[22ch] text-[1.5rem] leading-[1.16] font-semibold tracking-[-0.02em] sm:text-[1.9rem]`}
      >
        {head}
      </Heading>
      <p className={`mt-3 max-w-[52ch] ${INK_72} text-[0.95rem] leading-[1.65]`}>
        {children}
      </p>
    </div>
  );
}

/* ================================================================= */
/*  PGE-001 · The story page                                          */
/* ================================================================= */

/** The five pillars in the plain register, digest form. */
export const PILLAR_DIGEST: { plain: string; line: string; ref: string }[] = [
  {
    plain: "Lives with your business",
    line: "Always on, on a server that is yours alone, UK, EU or US.",
    ref: "PIL-001",
  },
  {
    plain: "Meaning, written down",
    line: "It proposes definitions in plain words; every version is kept and inspectable.",
    ref: "PIL-002",
  },
  {
    plain: "Any AI, your choice",
    line: "OpenAI, Anthropic, Google or your own hardware, swap models, keep the meaning.",
    ref: "PIL-003",
  },
  {
    plain: "Files records, not threads",
    line: "Agents must write into tables you define, so knowledge stays findable.",
    ref: "PIL-004",
  },
  {
    plain: "In the tools you already use",
    line: "MLX, Claude, ChatGPT, terminal or code, shared business context, controlled access.",
    ref: "PIL-005",
  },
];

function StoryPage() {
  return (
    <div className="px-5 sm:px-8 md:px-12">
      {/* Hero */}
      <header className="border-b border-dotted border-[var(--rule)] py-14 sm:py-16">
        <p className={`${INK_58} ${LABEL_CLS}`}>
          MLX · page prototype, the story, on one scroll
        </p>
        <h2
          style={DISPLAY}
          className={`${INK_88} mt-5 max-w-[16ch] text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.02] font-semibold tracking-[-0.04em]`}
        >
          AI that answers from what you approved.
        </h2>
        <p className={`mt-5 max-w-[54ch] ${INK_72} text-[1.05rem] leading-[1.65]`}>
          MLX reads your business systems, read-only. Your team approves what
          things mean. Every answer is built from that approved meaning,
          carries its receipts, and files its work as records you keep.
        </p>
        <div className="mt-7">
          <CtaPair />
        </div>
      </header>

      {/* 01 · Ask */}
      <section className="grid gap-8 border-b border-dotted border-[var(--rule)] py-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center">
        <BandRail index="01 · Ask" head="Ask in a sentence. Read the whole working.">
          One question. The answer comes from your approved definitions (never
          from the model's memory) and under it sits the receipt: which
          sources, which definition, which version. If you want to check it,
          the trace is one click away.
        </BandRail>
        <ProductBridge appearance="dark" className="px-5 py-6 sm:px-7">
          <div className="mx-auto w-full max-w-[560px]">
            <QuestionBubble>{RECEIVABLES_QUESTION}</QuestionBubble>
            <div className="mt-4">
              <CondensedAnswer>{CONDENSED_ANSWER_MARKDOWN}</CondensedAnswer>
            </div>
            <RunSourcesUsedView
              className="mt-3"
              sources={RECEIPT_SOURCES}
              plugins={RECEIPT_PLUGINS}
            />
          </div>
        </ProductBridge>
      </section>

      {/* 02 · Keep */}
      <section className="grid gap-8 border-b border-dotted border-[var(--rule)] py-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center">
        <BandRail index="02 · Keep" head="It files records, not chat threads.">
          The more your team uses AI, the more it produces, and unstructured
          output becomes the overhead. In MLX you define the shape once, and
          every agent must file typed records into it: findable, comparable,
          one current version by construction.
        </BandRail>
        <div className="grid min-w-0 gap-4 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          <div className="min-w-0">
            <p className={`${INK_58} ${DATA_LABEL_CLS} mb-2`}>Without MLX</p>
            <ul className="m-0 grid list-none gap-1.5 p-0">
              {SPRAWL_SLIPS.slice(0, 3).map((slip) => (
                <li
                  key={slip.place}
                  className="border border-dotted border-[var(--rule)] px-3 py-1.5"
                >
                  <p className={`${INK_58} ${DATA_LABEL_CLS}`}>{slip.place}</p>
                  <p className={`mt-0.5 ${INK_72} text-[0.78rem] leading-[1.45]`}>
                    {slip.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          <div className="min-w-0">
            <p className={`${INK_58} ${DATA_LABEL_CLS} mb-2`}>With MLX</p>
            <ProductBridge className="border border-border">
              <div className="flex min-w-0 flex-col">
                <RecordTableCompact />
              </div>
            </ProductBridge>
          </div>
        </div>
      </section>

      {/* 03 · The system */}
      <section className="border-b border-dotted border-[var(--rule)] py-12">
        <BandRail index="03 · The system" head="Five parts. One Twin.">
          Everything above is one system, a governed copy of your business
          that AI can safely use. Five parts carry it, and each has its own
          sheet on this board.
        </BandRail>
        <dl className="m-0 mt-7 grid gap-px bg-[var(--rule)] sm:grid-cols-2 lg:grid-cols-5">
          {PILLAR_DIGEST.map((pillar) => (
            <div key={pillar.ref} className="bg-[var(--theme-ground)] px-4 py-4">
              <dt className={`${INK_88} text-[0.9rem] leading-[1.35] font-semibold`}>
                {pillar.plain}
              </dt>
              <dd className={`m-0 mt-1.5 ${INK_72} text-[0.78rem] leading-[1.55]`}>
                {pillar.line}
              </dd>
              <dd className={`m-0 mt-2 ${INK_58} ${DATA_LABEL_CLS}`}>{pillar.ref}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* 04 · Trust */}
      <section className="grid gap-8 border-b border-dotted border-[var(--rule)] py-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center">
        <BandRail index="04 · Trust" head="It never guesses.">
          Ask for something your approved data cannot support and MLX stops,
          says why, and offers a question it can answer. For a business that
          runs on confidential, regulated information, the refusal is the
          feature.
        </BandRail>
        <div className="min-w-0">
          <ProductBridge className="px-5 py-6 sm:px-7">
            <div className="mx-auto w-full max-w-[560px]">
              <GovernedNonAnswerableCard outcome={REFUSAL_OUTCOME} />
            </div>
          </ProductBridge>
          <p className={`mt-2.5 ${INK_72} text-[0.75rem] leading-[1.5]`}>
            {REFUSAL_FIXTURE.hardStop} The card above is the product's own
            component, unchanged.
          </p>
        </div>
      </section>

      {/* Close */}
      <section
        className="my-12 px-6 py-12 sm:px-10"
        style={{
          backgroundColor: "var(--theme-surface-dark)",
          // Headings obey the unlayered data-editorial inherit rule, so the
          // band sets the ink and the H3 inherits it.
          color: "var(--theme-ground)",
        }}
      >
        <p className={`${LABEL_CLS} text-[var(--theme-accent)]`}>
          Two weeks to first value
        </p>
        <h3
          style={DISPLAY}
          className={`${GINK_82} mt-4 max-w-[20ch] text-[1.8rem] leading-[1.1] font-semibold tracking-[-0.025em] sm:text-[2.3rem]`}
        >
          See it on your own numbers.
        </h3>
        <p className={`mt-3 max-w-[52ch] ${GINK_82} text-[0.95rem] leading-[1.6] opacity-80`}>
          Connect a system read-only, approve your first definitions, and ask.
          If MLX cannot earn the answer, it will be the first to say so.
        </p>
        <div className="mt-7">
          <CtaPair onDark />
        </div>
      </section>

      <p className={`${INK_58} pb-6 text-[0.72rem] leading-[1.5]`}>
        PGE-001 is a page prototype assembled from the board's real components
, the answer, receipt, records and refusal are the product's own,
        rendered from the same fixtures as the sheets they came from.
      </p>
    </div>
  );
}

/* ================================================================= */
/*  PGE-002 · The living specimen                                     */
/* ================================================================= */

/**
 * The specimen streams through the product's own path: the source grows in
 * slices, normalized with the streaming flag, and the application's
 * useStreamingMarkdownRehypePlugins wraps each new run in the real
 * .mlx-stream-text-run wipe (stream-text.css, imported from the app).
 */
export function StreamingAnswer({
  source,
  streaming,
  identity,
}: {
  source: string;
  streaming: boolean;
  identity: string;
}) {
  const normalized = normalizeMarkdownContent(source, { streaming });
  const streamingRehypePlugins = useStreamingMarkdownRehypePlugins({
    enabled: streaming,
    source: normalized,
    streamIdentity: identity,
  });
  const rehypePlugins = streamingRehypePlugins
    ? [...streamingRehypePlugins, ...markdownRehypePlugins]
    : markdownRehypePlugins;
  return (
    <div
      className="typeset typeset-chat max-w-[58ch] break-words whitespace-normal"
      data-streaming={streaming ? "true" : undefined}
    >
      <ReactMarkdown
        remarkPlugins={markdownRemarkPlugins}
        rehypePlugins={rehypePlugins}
        components={staticMarkdownRendererComponents}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}

export const REVEAL_STEP_CHARS = 48;
export const REVEAL_TICK_MS = 110;

function Staged({
  visible,
  children,
}: {
  visible: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`transition-all duration-500 motion-reduce:transition-none ${
        visible ? "translate-y-0 opacity-100" : "translate-y-1.5 opacity-0"
      }`}
    >
      {children}
    </div>
  );
}

function LivingSpecimen() {
  // Mounts settled (the finished turn) so prints, snapshots and
  // reduced-motion readers see everything; Replay streams it through the
  // product's own wipe.
  const [run, setRun] = useState(0);
  const [revealed, setRevealed] = useState(CONDENSED_ANSWER_MARKDOWN.length);
  const [tail, setTail] = useState(2);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);

  const playing = revealed < CONDENSED_ANSWER_MARKDOWN.length;

  const clear = useCallback(() => {
    for (const timer of timers.current) clearTimeout(timer);
    timers.current = [];
    if (ticker.current) clearInterval(ticker.current);
    ticker.current = null;
  }, []);

  useEffect(() => clear, [clear]);

  const replay = useCallback(() => {
    clear();
    setRun((count) => count + 1);
    setRevealed(0);
    setTail(0);
    // The question lands first; the stream starts on its heels.
    timers.current.push(
      setTimeout(() => {
        ticker.current = setInterval(() => {
          setRevealed((current) => {
            const next = Math.min(
              current + REVEAL_STEP_CHARS,
              CONDENSED_ANSWER_MARKDOWN.length,
            );
            if (next >= CONDENSED_ANSWER_MARKDOWN.length) {
              if (ticker.current) clearInterval(ticker.current);
              ticker.current = null;
              timers.current.push(setTimeout(() => setTail(1), 350));
              timers.current.push(setTimeout(() => setTail(2), 1150));
            }
            return next;
          });
        }, REVEAL_TICK_MS);
      }, 600),
    );
  }, [clear]);

  return (
    <section>
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-[46rem]">
          <p className={`${INK_72} ${LABEL_CLS}`}>Session · staged</p>
          <h3
            style={DISPLAY}
            className={`${INK_88} mt-3 text-[1.65rem] leading-[1.16] font-semibold tracking-[-0.02em] sm:text-[2rem]`}
          >
            One turn, watched.
          </h3>
          <p className={`mt-3 max-w-[58ch] ${INK_72} text-[0.95rem] leading-[1.6]`}>
            The whole loop in four seconds: the question lands, the governed
            answer arrives, the receipt attaches, and the work is filed. Every
            element is the real component; only the timing is staged.
          </p>
        </div>
        <button
          type="button"
          onClick={replay}
          className={`inline-flex shrink-0 items-center gap-2 border border-[var(--rule)] px-4 py-2 text-[0.78rem] font-medium ${INK_80} transition-colors hover:border-[var(--theme-accent-text)] hover:text-[var(--theme-accent-text)]`}
        >
          ▶ Replay the turn
        </button>
      </header>

      <figure className="m-0 border border-[var(--rule)]">
        <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem]">
          <span className={`${INK_80} ${DATA_LABEL_CLS}`}>
            Exhibit · the loop, staged
          </span>
          <strong className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
            Real components · staged timing
          </strong>
        </div>
        <ProductBridge appearance="dark" className="px-5 py-6 sm:px-7">
          <div className="mx-auto grid w-full max-w-[640px] gap-4">
            <Staged visible>
              <QuestionBubble>{RECEIVABLES_QUESTION}</QuestionBubble>
            </Staged>
            <StreamingAnswer
              source={CONDENSED_ANSWER_MARKDOWN.slice(0, revealed)}
              streaming={playing}
              identity={`living-specimen-${run}`}
            />
            <Staged visible={tail >= 1}>
              <RunSourcesUsedView
                sources={RECEIPT_SOURCES}
                plugins={RECEIPT_PLUGINS}
              />
            </Staged>
            <Staged visible={tail >= 2}>
              <div className="max-w-[420px] border-t border-border/60 pt-4">
                <p className="mb-2 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                  Filed with the Session
                </p>
                <ArtifactCard artifact={RECEIVABLES_ARTIFACTS[0]!} />
              </div>
            </Staged>
          </div>
        </ProductBridge>
        <p
          className={`border-t border-dotted border-[var(--rule)] px-4 py-[0.7rem] ${INK_80} text-[0.72rem] leading-[1.45]`}
        >
          The specimen mounts settled, so prints and reduced-motion readers see the finished turn; Replay streams it. The reveal is the product's own: the application's streaming plugin wraps each new run in its real mlx-stream-text-run wipe, whose CSS is imported from the app, question, answer, receipt and artifact are the same real components and fixtures as SES-001 and SES-003.
        </p>
      </figure>
    </section>
  );
}

/* ================================================================= */

export const PAGE_GROUP: ConceptGroup = {
  id: "pages",
  prefix: "PGE",
  title: "Page prototypes",
  blurb:
    "Assemblies, not components: the board's real material composed the way a visitor would meet it. The story page walks a cold reader from plain-words claim to governed proof in one scroll; the living specimen stages a single turn so the loop can be watched, not inferred. Both speak the plain register (TON-017) and the settled surface language, light sections, dark product assets, one canonical night.",
  specs: [
    {
      id: "PGE-001",
      title: "The story page",
      note: "One scroll from cold to convinced: plain-words hero, the governed answer as a dark asset, records-not-threads, the five pillars in digest, the refusal as the trust close, and a night CTA band.",
      surface: "ground",
      width: "full",
      interaction: "static",
      state: "One scroll",
      render: () => <StoryPage />,
    },
    {
      id: "PGE-002",
      title: "The living specimen",
      note: "The governed turn staged in four seconds (question, answer, receipt, filed artifact) on the real components, with a Replay control and a settled initial state for print and reduced motion.",
      surface: "ground",
      width: "full",
      interaction: "prototype",
      state: "Staged turn",
      render: () => <LivingSpecimen />,
    },
  ],
};
